import { updatePersonLegal } from '@dealstack/frontend/app/services/Person.service.ts';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { useMutation } from '@tanstack/react-query';

interface Params {
	personId: string;
	/** Refetches the entity so the switch reflects the persisted flag. */
	refetchSelectedEntity: () => void;
	/** Refetches the securities list — each row's nominee capability derives from this flag. */
	refetchSecurities: () => void;
}

/**
 * CAP-1976: writes `hasNomineeStructure` on ONE legal person — the entity whose Securities tab is
 * open. This is the flag the backend reads off a security's ISSUER to decide whether that security
 * supports split legal/beneficial titles (`isNomineeStructureSecurity`), so it belongs on the entity
 * being viewed rather than in a deal-wide list. `Deal.hasNomineeStructure` remains the master gate
 * above it.
 */
export const useEntityNomineeStructure = ({
	personId,
	refetchSelectedEntity,
	refetchSecurities,
}: Params) => {
	const tCapManage = useTranslationCapManage();

	const updateRequest = useMutation({
		mutationFn: (hasNomineeStructure: boolean) =>
			updatePersonLegal({ personId, updateArgs: { hasNomineeStructure } }),
	});

	const setHasNomineeStructure = (next: boolean) => {
		updateRequest.mutate(next, {
			onSuccess: () => {
				refetchSelectedEntity();
				refetchSecurities();
				showToastV2({
					title: next
						? tCapManage('securities.nominee_structure.toast.enabled')
						: tCapManage('securities.nominee_structure.toast.disabled'),
				});
			},
			onError: () => {
				showToastV2({
					state: 'error',
					title: tCapManage('securities.nominee_structure.toast.error'),
				});
			},
		});
	};

	return {
		setHasNomineeStructure,
		isSaving: updateRequest.isPending,
	};
};
